import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProducerSettings } from "@/hooks/useProducerSettings";

interface Props {
  settings: ProducerSettings;
  onUpdate: (updates: Partial<ProducerSettings>) => void;
}

export function ProducerScheduleSettings({ settings, onUpdate }: Props) {
  return (
    <Card className="space-y-6 p-6">
      <div>
        <h2 className="mb-2 text-xl font-semibold">Schedule settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure how the production schedule timeline is displayed.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Time format</Label>
        <Select
          value={settings.schedule_time_format}
          onValueChange={(value: "24h" | "12h") => onUpdate({ schedule_time_format: value })}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">24-hour (22:00)</SelectItem>
            <SelectItem value="12h">12-hour (10PM)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Default assignment length</Label>
        <Select
          value={(settings.default_assignment_length || 15).toString()}
          onValueChange={(value) => onUpdate({ default_assignment_length: parseInt(value) })}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="45">45 minutes</SelectItem>
            <SelectItem value="60">60 minutes</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          Default duration when manually assigning crew to artists
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Display options</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="time_marker" className="block">
                Show current time marker
              </Label>
              <p className="text-sm text-muted-foreground">
                Display a vertical line at the current time
              </p>
            </div>
            <Switch
              id="time_marker"
              checked={settings.schedule_show_current_time_marker}
              onCheckedChange={(checked) =>
                onUpdate({ schedule_show_current_time_marker: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto_scroll" className="block">
                Auto-scroll to current time
              </Label>
              <p className="text-sm text-muted-foreground">
                Automatically scroll timeline to now when opening
              </p>
            </div>
            <Switch
              id="auto_scroll"
              checked={settings.schedule_auto_scroll_current_time}
              onCheckedChange={(checked) =>
                onUpdate({ schedule_auto_scroll_current_time: checked })
              }
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
